/**
 * The AI and Design strips move in opposite directions, then rest. Both rows arrive together.
 * The production composition supplies the curve and eight-second cycle; the move takes 10% longer
 * to soften the pace. The border turns around the card without rest, one turn every two seconds,
 * independent of the rows: its trim offsets carry `loopOut()` in the composition.
 *
 * A row is four identical copies of its strip, so a step of one strip width lands on the same picture:
 * the loop shows no seam. The step is a `translate` on the compositor. The border is three dash
 * windows on an SVG outline, and a dash offset paints on the main thread: one card-sized region, each
 * turn one finite run that starts the next. Off screen the piece stops; on screen it starts from the top.
 */
import {EASING_CURVES} from '../../modules/motion/easing.js';
import {createRun} from '../../modules/motion/run.js';

const SELECTOR = Object.freeze({
    root: '.js-motionAiAndDesign',
    row: '.js-motionAiAndDesignRow',
    border: '.js-motionAiAndDesignBorder',
});

/** Milliseconds of one loop, and the cues inside it. */
const TIMING = Object.freeze({
    loop: 8000,
    step: 2200,
    /* One turn of the border. Its strokes leave one after the other and arrive together, as in the composition. */
    turnCycle: 1960,
    turnEnd: 1920,
    turnStart: [0, 160, 280],
});

/**
 * The border as the composition draws it: three windows of the outline, one per stroke from the widest,
 * that share a front. Where the front is when the strokes arrive together, as a point on an edge, clockwise.
 */
const BORDER = Object.freeze({
    front: {edge: 'top', at: 0.8},
    windows: [60, 65, 73],
});

const EASE = Object.freeze({
    /* The composition's curve for a step: a gentle start, an early peak, and a long landing. */
    step: EASING_CURVES['ease-step'],
    /* The composition's turn curve: front-loaded, so the staggered strokes pull apart early and land together. */
    turn: EASING_CURVES['ease-turn'],
});

/** @type {WeakMap<HTMLElement, import('../../modules/motion/run.js').Run>} The step of the rows. */
const runs = new WeakMap();
/** @type {WeakMap<HTMLElement, import('../../modules/motion/run.js').Run>} The independent turn of the borders. */
const turns = new WeakMap();

/** Which copy of the strip rests in view: two copies stay to its left, so the row covers the screen at both ends of a step. */
const RESTING_COPY = 2;

/**
 * The translate at rest: a row with a highlighted card rests with that card in the middle of the screen.
 * @param {HTMLElement} row
 * @returns {number} Pixels.
 */
function restShift(row) {
    const highlighted = row.children[RESTING_COPY].querySelector('[data-highlighted]');
    if (!(highlighted instanceof HTMLElement)) {
        return 0;
    }
    // Measured against the row's first strip, so the root's margin on a wide screen does not count; both
    // rects carry the row's current translate, so it cancels out. Fractions matter: a rounded width is a seam.
    const card = highlighted.getBoundingClientRect();
    const center = card.left - row.firstElementChild.getBoundingClientRect().left + card.width / 2;

    return row.parentElement.clientWidth / 2 - center;
}

/**
 * @param {HTMLElement} row
 * @returns {Animation}
 */
function stepRow(row) {
    const [first, second] = row.children;
    // A strip is text, so its natural width is fractional; then every copy sits on its own subpixel phase
    // and rasterizes a little differently, and the seam shows as a shimmer. Whole pixels give one phase.
    for (const strip of row.children) {
        strip.style.width = '';
        strip.style.width = `${Math.ceil(strip.getBoundingClientRect().width)}px`;
    }
    // The strip width plus its gap: the distance from one copy to the next.
    const period = second.getBoundingClientRect().left - first.getBoundingClientRect().left;
    const rest = restShift(row);
    const from = `${row.dataset.direction === 'start' ? rest + period : rest - period}px`;
    const to = `${rest}px`;

    return row.animate(
        [
            // Keyframe easing applies to the segment that follows it, not the segment before it.
            {translate: from, easing: EASE.step},
            {translate: to, offset: TIMING.step / TIMING.loop},
            {translate: to},
        ],
        // No fill: the end of a step is the same picture as the start of the next run.
        {duration: TIMING.loop}
    );
}

/**
 * The distance along the card's outline, as a percentage of it, to a point on an edge. The outline
 * starts where the top edge leaves the top-left corner and runs clockwise, as an SVG rect does.
 * @param {{width: number, height: number, radius: number}} card
 * @param {{edge: 'top'|'right'|'bottom'|'left', at: number}} point `at` is the fraction of the edge, in reading direction.
 * @returns {number}
 */
function along(card, point) {
    const {width, height, radius} = card;
    const corner = (Math.PI * radius) / 2;
    const top = width - 2 * radius;
    const side = height - 2 * radius;
    const perimeter = 2 * top + 2 * side + 4 * corner;
    const distance = {
        top: point.at * width - radius,
        right: top + corner + point.at * height - radius,
        bottom: top + corner + side + corner + (1 - point.at) * width - radius,
        left: 2 * top + 3 * corner + side + (1 - point.at) * height - radius,
    }[point.edge];

    return (100 * distance) / perimeter;
}

/**
 * Size each stroke's window for this card, then turn all three once around it. The dash pattern is
 * the outline's length, so one turn of the offset is the same picture as none: no fill.
 * @param {SVGSVGElement} border
 * @returns {Animation[]}
 */
function turnBorder(border) {
    const parent = border.parentElement;
    const card = {
        width: parent.offsetWidth,
        height: parent.offsetHeight,
        radius: Number.parseFloat(getComputedStyle(parent).borderTopLeftRadius),
    };
    const front = along(card, BORDER.front);

    return [...border.children].map((stroke, index) => {
        stroke.setAttribute('rx', `${card.radius}`);
        const length = BORDER.windows[index];
        const tail = (front - length + 100) % 100;
        stroke.setAttribute('stroke-dasharray', `${length} ${100 - length}`);
        stroke.setAttribute('stroke-dashoffset', `${-tail}`);

        return stroke.animate([{strokeDashoffset: -tail, easing: EASE.turn}, {strokeDashoffset: -tail - 100}], {
            delay: TIMING.turnCycle - TIMING.turnEnd + TIMING.turnStart[index],
            duration: TIMING.turnEnd - TIMING.turnStart[index],
        });
    });
}

/**
 * Keep both rows in one run so they arrive together on every cycle.
 * @param {HTMLElement} root
 */
function step(root) {
    const run = createRun();
    for (const row of root.querySelectorAll(SELECTOR.row)) {
        if (row instanceof HTMLElement) {
            run.add(stepRow(row));
        }
    }
    runs.set(root, run);
    run.settled().then(() => {
        if (runs.get(root) === run) {
            step(root);
        }
    });
}

/**
 * Turn every border once, then again.
 * @param {HTMLElement} root
 */
function turn(root) {
    const run = createRun();
    for (const border of root.querySelectorAll(SELECTOR.border)) {
        if (border instanceof SVGSVGElement) {
            run.add(...turnBorder(border));
        }
    }
    turns.set(root, run);
    run.settled().then(() => {
        if (turns.get(root) === run) {
            turn(root);
        }
    });
}

/**
 * @param {HTMLElement} root
 */
function start(root) {
    stop(root);
    step(root);
    turn(root);
}

/**
 * @param {HTMLElement} root
 */
function stop(root) {
    runs.get(root)?.cancel();
    runs.delete(root);
    turns.get(root)?.cancel();
    turns.delete(root);
}

/**
 * @param {HTMLElement} root
 */
function initialize(root) {
    if (root.dataset.motionState !== undefined) {
        return;
    }
    root.dataset.motionState = 'idle';

    const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
            start(root);
            root.dataset.motionState = 'running';
        } else {
            stop(root);
            root.dataset.motionState = 'idle';
            // A root that left the document (a content swap) releases its observer.
            if (!root.isConnected) {
                observer.disconnect();
            }
        }
    });
    observer.observe(root);

    // A new width changes the step distance: a running piece starts again at the new size.
    let width = root.offsetWidth;
    new ResizeObserver(() => {
        if (root.offsetWidth === width || root.dataset.motionState !== 'running') {
            return;
        }
        width = root.offsetWidth;
        start(root);
    }).observe(root);
}

/**
 * Initialize every piece that is not initialized yet. Safe to call after content swaps.
 */
export function initializeComponent() {
    for (const root of document.querySelectorAll(SELECTOR.root)) {
        if (root instanceof HTMLElement) {
            initialize(root);
        }
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeComponent);
} else {
    initializeComponent();
}
document.addEventListener('htmx:load', initializeComponent);
